import { createApp } from "vue";
import App from "./App.vue";
import "./registerServiceWorker";
import router from "./router";
import { createPinia } from "pinia";
import { createI18n } from "vue-i18n";
import EN from "@/locale/en.json";
import AR from "@/locale/ar.json";
import VueGoogleMaps from "@fawmi/vue-google-maps";
import socket from "@/core/socketIo.js";
import { watch } from "vue";
import { loginForm } from "@/store/logIn";

router.afterEach((to, from) => {
  if (to.name !== "Messagescenter" && socket.connected) {
    socket.disconnect();
    console.log("✅ Socket disconnected (not on Messagescenter)");
  } else if (to.name === "Messagescenter" && !socket.connected) {
    socket.connect();
    console.log("🔄 Socket reconnected (on Messagescenter)");
  }
});

// Vuetify
import "@mdi/font/css/materialdesignicons.css";
import "vuetify/styles";
import { createVuetify } from "vuetify";
import * as components from "vuetify/components";
import * as directives from "vuetify/directives";
const vuetify = createVuetify({
  components,
  directives,
});

import { library } from "@fortawesome/fontawesome-svg-core";
import { faUserSecret } from "@fortawesome/free-solid-svg-icons";

library.add(faUserSecret);

import { faCoffee } from "@fortawesome/free-solid-svg-icons";
library.add(faCoffee);

import { faSquareFacebook } from "@fortawesome/free-brands-svg-icons";
library.add(faSquareFacebook);

import "vue3-toastify/dist/index.css";
import apiClient from "@/core/buildRequest";

const i18n = createI18n({
  legacy: false,
  locale: localStorage.getItem("lang")?.toLowerCase() || "en",
  fallbackLocale: "en",
  messages: {
    en: EN,
    ar: AR,
  },
});

router.beforeEach((to, from, next) => {
  if (to.meta?.title) {
    document.title = i18n.global.t(to.meta.title);
  }
  next();
});
watch(
  () => i18n.global.locale.value,
  (newLocale) => {
    const currentRoute = router.currentRoute.value;
    if (currentRoute.meta?.title) {
      document.title = i18n.global.t(currentRoute.meta.title);
    }

    // ✅ لو حبيت تحدث اتجاه الصفحة برضو:
    document.body.style.direction = newLocale === "ar" ? "rtl" : "ltr";
    document.documentElement.setAttribute("lang", newLocale);
  }
);

document.body.style.direction =
  localStorage.getItem("lang") === "ar" ? "rtl" : "ltr";
document.documentElement.setAttribute(
  "lang",
  localStorage.getItem("lang") === "ar" ? "ar" : "en"
);

const cleanLocalStorage = () => {
  for (const key of Object.keys(localStorage)) {
    if (key !== "deviceId") {
      localStorage.removeItem(key);
    }
  }
};

const handleCredentialError = (error) => {
  console.error("Error fetching credentials:", error);
  cleanLocalStorage();
};

function storeToken(service, token, expireDate) {
  localStorage.setItem(`${service}_token`, token);
  localStorage.setItem(`${service}TokenExpireAt`, expireDate);
}

async function initializeApp() {
  if (
    localStorage.getItem("user_token") &&
    localStorage.getItem("user_token") !== ""
  ) {
    try {
      const { data } = await apiClient.get("/check-user-token");
      if (data.data.user_id) {
        let c = JSON.parse(localStorage.getItem("user"));
        if (c["data"].id === data.data.user_id) {
          try {
            const { data } = await apiClient.get("/get-credentials");
            const credentials = data.data;
            [
              "snapchat",
              "tiktok",
              "facebook",
              "instagram",
              "twitter",
              "google_ads",
              "drive",
            ].forEach((service) => {
              if (credentials[service]) {
                storeToken(
                  service,
                  credentials[service].access_token,
                  credentials[service].expire_date
                );
              }
            });
          } catch (error) {
            handleCredentialError(error);
          }
        } else {
          cleanLocalStorage();
        }
      } else {
        cleanLocalStorage();
      }
    } catch (error) {
      handleCredentialError(error);
    }
  } else {
    cleanLocalStorage();
  }
}

const app = createApp(App)
  .use(vuetify)
  .use(router)
  .use(createPinia())
  .use(i18n)
  .use(VueGoogleMaps, {
    load: {
      key: process.env.VUE_APP_GOOGLE_MAPS_API_KEY,
      libraries: "places",
    },
  });

app.config.globalProperties.$permissionsFor = (moduleName) => {
  const loginStore = loginForm();
  const actions = ["create", "update", "delete", "show"];
  const has = (a) =>
    loginStore?.permissions?.includes(`${moduleName}.${a}`) ?? false;

  const map = Object.fromEntries(actions.map((a) => [a, has(a)]));
  const any = actions.some((a) => map[a]);
  const all = actions.every((a) => map[a]);

  return {
    ...map,
    any,
    all,
    has: (acts) => {
      if (Array.isArray(acts)) return acts.some((a) => map[a]);
      return !!map[acts];
    },
    list: actions.filter((a) => map[a]),
    missing: actions.filter((a) => !map[a]),
  };
};

// app.config.globalProperties.$hasAnyPermission = (
//   moduleName,
//   actions = ["create", "edit", "delete", "show"]
// ) => {
//   const loginStore = loginForm();
//   const requiredPermissions = actions.map(
//     (action) => `${moduleName}.${action}`
//   );
//   return requiredPermissions.some((p) => loginStore?.permissions?.includes(p));
// };
initializeApp()
  .then(() => {
    app.mount("#app");
  })
  .catch((error) => {
    console.error("Failed to initialize app:", error);
    app.mount("#app");
  });
